import { CategoryButton } from 'spa/components/button/category_button'
import { connect } from 'react-redux'
import { fetchCategories, fetchLocaleTagsThunk } from 'spa/reducers/categories'
import { REGION_IDS } from 'spa/constants/constants'
import { standardFiltersShape } from 'spa/api/songs'
import { toNumericGenreIds } from 'spa/shared/utils'
import classNames from 'classnames'
import intersection from 'lodash/intersection'
import PropTypes from 'prop-types'
import React from 'react'
import styles from './styles'

class DynamicCategoryFilterPanel extends React.PureComponent {
  static propTypes = {
    categories: PropTypes.array,
    children: PropTypes.node,
    fetchCategories: PropTypes.func.isRequired,
    fetchLocaleTags: PropTypes.func.isRequired,
    filters: PropTypes.shape(standardFiltersShape),
    loading: PropTypes.object,
    locale: PropTypes.string,
    localeTags: PropTypes.object,
    onChange: PropTypes.func,
    showLocaleButtons: PropTypes.bool,
  }

  static defaultProps = {
    showLocaleButtons: true,
  }

  state = {
    categoriesLoaded: false,
  }

  componentDidMount() {
    const {
      categories,
      localeTags,
      loading,
      fetchCategories,
      fetchLocaleTags,
    } = this.props

    if (loading.categories || categories.length === 0) {
      fetchCategories()
    }

    if (loading.localeTags || Object.keys(localeTags).length === 0) {
      fetchLocaleTags()
    }

    // Set a small delay to trigger the animation after mount
    setTimeout(() => {
      this.setState({ categoriesLoaded: true })
    }, 100)
  }

  componentDidUpdate(prevProps) {
    const { locale, categories } = this.props

    if (prevProps.locale !== locale) {
      this.handleGenreChange([], 0)
    }

    // Reset animation when categories change
    if (prevProps.categories.length !== categories.length) {
      this.setState({ categoriesLoaded: false })
      setTimeout(() => {
        this.setState({ categoriesLoaded: true })
      }, 100)
    }
  }

  handleGenreChange = (genres, regionId) => {
    const { filters, onChange } = this.props

    const numericGenres = toNumericGenreIds(genres)

    onChange?.({ ...filters, genres: numericGenres, regionId, page: 1 })
  }

  handleChange = (tagIds, regionId, checked) => {
    const { filters: { genres = [] } = {} } = this.props

    if (checked) {
      this.handleGenreChange(tagIds, regionId)
    } else {
      const numericCurrentGenres = toNumericGenreIds(genres)
      const numericTagIds = toNumericGenreIds(tagIds)

      const newGenres = numericCurrentGenres.filter(
        genre => !numericTagIds.includes(genre)
      )

      this.handleGenreChange(newGenres, regionId ?? null)
    }
  }

  GenreButton = (category, index) => {
    const { filters: { genres } = {} } = this.props
    const { categoriesLoaded } = this.state
    const isChecked =
      intersection(genres.map(String), category.tagIds).length > 0

    const buttonClasses = classNames(
      styles.categoryButtonWrapper,
      styles[`index-${index}`],
      {
        [styles.hidden]: !categoriesLoaded,
        [styles.visible]: categoriesLoaded,
      }
    )

    const buttonId = `category-${category.id}`

    return (
      <div className={buttonClasses} key={category.id}>
        <CategoryButton
          checked={isChecked}
          className={styles.noSelect}
          id={buttonId}
          labelText={category.name}
          onChange={e =>
            this.handleChange(category.tagIds, 0, e.target.checked)
          }
        />
      </div>
    )
  }

  getLocaleDisplayName = code => {
    const displayNames = {
      GB: 'UK',
      JP: '日本',
      DE: 'DE',
    }
    return displayNames[code] || code
  }

  render() {
    const {
      children,
      filters: { genres } = {},
      locale,
      showLocaleButtons,
      categories,
      localeTags,
      loading,
    } = this.props

    if (
      loading.categories &&
      loading.localeTags &&
      categories.length === 0 &&
      Object.keys(localeTags).length === 0
    ) {
      return null
    }

    const localeCode = locale?.slice(3, 5)

    const localeTagObject = Array.isArray(localeTags)
      ? localeTags.find(tag => tag.name === localeCode)
      : null

    const localeTagIds = localeTagObject?.tagIds || []

    const filteredCategories = categories.filter(
      category => category.tagIds?.length > 0
    )
    const buttons = filteredCategories.map(this.GenreButton)

    if (showLocaleButtons && localeTagIds.length > 0) {
      const { categoriesLoaded } = this.state
      const localeButtonIndex = filteredCategories.length

      const localeButtonClasses = classNames(
        styles.categoryButtonWrapper,
        styles[`index-${localeButtonIndex}`],
        {
          [styles.hidden]: !categoriesLoaded,
          [styles.visible]: categoriesLoaded,
        }
      )

      const localeButtonId = `locale-${localeCode}`

      const localeButton = (
        <div className={localeButtonClasses} key={localeCode}>
          <CategoryButton
            checked={
              intersection(genres?.map(String), localeTagIds.map(String))
                .length > 0
            }
            className={styles.noSelect}
            id={localeButtonId}
            labelText={this.getLocaleDisplayName(localeCode)}
            onChange={e => {
              this.handleChange(
                localeTagIds,
                REGION_IDS[localeCode],
                e.target.checked
              )
            }}
          />
        </div>
      )
      buttons.push(localeButton)
    }

    const otherButtonIndex = buttons.length
    const otherButtonClasses = classNames(
      styles.categoryButtonWrapper,
      styles[`index-${otherButtonIndex}`],
      {
        [styles.hidden]: !this.state.categoriesLoaded,
        [styles.visible]: this.state.categoriesLoaded,
      }
    )

    // Create a consistent ID for the "Other" button
    const otherButtonId = 'category-other'

    return (
      <div className={styles.checkboxRow}>
        {buttons}
        {children}
        <div className={otherButtonClasses} key="other">
          <CategoryButton
            checked={intersection(genres?.map(String), ['8']).length > 0}
            className={styles.noSelect}
            id={otherButtonId}
            labelText="Other"
            onChange={e => this.handleChange(['8'], 0, e.target.checked)}
          />
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state, props) => ({
  locale: state.locale ? state.locale.locale : null,
  categories: state.categories.categories,
  localeTags: state.categories.localeTags,
  loading: state.categories.loading,
  ...props,
})

const mapDispatchToProps = {
  fetchCategories,
  fetchLocaleTags: fetchLocaleTagsThunk,
}

const ConnectedDynamicCategoryFilterPanel = connect(
  mapStateToProps,
  mapDispatchToProps
)(DynamicCategoryFilterPanel)
export { ConnectedDynamicCategoryFilterPanel as DynamicCategoryFilterPanel }
